import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import BrandLogo from "../components/BrandLogo";
import { useAuth } from "../context/AuthContext";
import { createWeddingHref } from "../utils/authRedirect";

const Home: React.FC = () => {
  const { hash } = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    if (hash !== "#how-it-works" && hash !== "#about") return;
    const id = hash.slice(1);
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  return (
    <div className="home-page">
      <section className="home-hero" aria-label="Ticketed Wedding">
        <div
          className="home-hero-media"
          role="img"
          aria-label="Couple celebrating at a wedding, photograph by Nathan Dumlao"
        />
        <div className="home-hero-scrim" aria-hidden="true" />
        <div className="home-hero-content">
          <BrandLogo variant="stacked" to={null} light className="home-hero-brand" />
          <h1 className="home-hero-headline">Your presence is the present.</h1>
          <p className="home-hero-support">
            Couples list their wedding. Guests RSVP and pay for their seat. You stay in
            control.
          </p>
          <div className="home-hero-cta">
            <Link to={createWeddingHref(Boolean(user))} className="btn btn-primary btn-lg">
              Create Your Wedding
            </Link>
            <Link
              to="/weddings/public?search=1"
              className="btn btn-on-dark btn-secondary btn-lg"
            >
              RSVP
            </Link>
          </div>
        </div>
      </section>

      <section className="home-about" id="about" aria-label="About Ticketed Wedding">
        <div className="home-about-inner">
          <div className="home-about-intro">
            <h2 className="home-about-headline">
              Everyone you&apos;d want there.
              <br />
              Actually there.
            </h2>
            <p>
              Picture the version of your wedding where the coworker you love, the college
              roommate, and the friend of a friend who lives for a good party are all in the
              room. That version usually doesn&apos;t survive the first budget meeting. More
              people means more money, so you start cutting people you were looking forward to
              celebrating with.
            </p>
            <p className="home-about-closer">We built a way to make more room.</p>
          </div>

          <div className="home-about-thread" aria-hidden="true" />

          <div className="home-about-block">
            <h3 className="home-about-eyebrow">The math of a guest list</h3>
            <p>
              Weddings are the best party you&apos;ll ever throw. They&apos;re also one of the
              most expensive. The average couple spends over $30,000, and every extra guest
              adds to the bill. So people you genuinely want there can end up getting cut
              simply because the numbers don&apos;t work.
            </p>
            <p>
              And the list has a way of growing. Future mother-in-law keeps adding names
              you&apos;ve never heard? Now there&apos;s a little more room for all of them.
            </p>
            <p>
              Most couples getting married today already live together. They&apos;ve got the
              toaster, the towels, and the good knives. What&apos;s harder to find is room in
              the budget to celebrate as big as they want, with everyone they want there.
            </p>
            <p>
              Ticketed Wedding gives couples another way to make that room. Some guests can
              have a free seat. Some can contribute toward theirs. And some couples can open a
              handful of seats to people beyond their usual circle.
            </p>
            <p className="home-about-beat">
              The couple decides what works for their wedding.
            </p>
          </div>

          <div className="home-about-thread home-about-thread-short" aria-hidden="true" />

          <div className="home-about-block">
            <h3 className="home-about-eyebrow">Who it&apos;s for</h3>
            <div className="home-about-audience">
              <span className="home-about-who">Couples</span>
              <p>
                Paying for their own wedding who want a celebration big enough for the people
                they actually want there. More room, more flexibility, and a full dance floor
                if that&apos;s what matters most.
              </p>
            </div>
            <div className="home-about-audience">
              <span className="home-about-who">Invited guests</span>
              <p>
                Who want an easy RSVP and a clear way to handle their seat if the couple has
                chosen a ticketed option. One link, one place, done.
              </p>
            </div>
            <div className="home-about-audience">
              <span className="home-about-who">People who love a good wedding</span>
              <p>
                The kind who&apos;d happily get dressed up, meet a roomful of new people, and
                say yes to an invitation they didn&apos;t see coming.
              </p>
            </div>
          </div>

          <div className="home-about-thread home-about-thread-short" aria-hidden="true" />

          <div className="home-about-block">
            <h3 className="home-about-eyebrow">If you found us looking for something to do</h3>
            <p>
              Wedding crashing used to take guts. These days, some couples are choosing to
              leave a few seats open for people they haven&apos;t met yet.
            </p>
            <p>
              If you find a wedding open to the public, send the couple a quick note about
              yourself and why you&apos;d love to join them. If they like what they hear,
              they&apos;ll approve your request. Then you pay for your seat and get an
              invitation to show up properly.
            </p>
            <p>
              Dress up. Dance with strangers. Raise a glass to two people who decided their
              celebration could use a few more stories in it.
            </p>
            <p className="home-about-beat">
              However you got here, there&apos;s room for a few more people when the couple
              decides to make it.
            </p>
          </div>

          <div className="home-about-thread" aria-hidden="true" />

          <footer className="home-about-signoff">
            <p className="home-about-campaign">Unwrappable. Unforgettable.</p>
          </footer>
        </div>
      </section>

      <section className="home-how" id="how-it-works">
        <div className="home-how-inner">
          <h2>How it works</h2>
          <div className="golden-thread" aria-hidden="true" />
          <p className="home-how-lead">
            Create your wedding, set the ticket price, and invite your guests. Guests pay
            for their tickets through Stripe, and you receive the funds through your
            connected account.
          </p>
          <div className="home-steps">
            <article className="home-step">
              <span className="home-step-num" aria-hidden="true">
                01
              </span>
              <div>
                <h3>Create your wedding</h3>
                <p>
                  Tell guests about your wedding, set your dress code and vibe, choose
                  your ticket tiers, and add the details they need to know.
                </p>
              </div>
            </article>
            <article className="home-step">
              <span className="home-step-num" aria-hidden="true">
                02
              </span>
              <div>
                <h3>Invite your guests</h3>
                <p>
                  Share your wedding with the people you want to invite. Guests can RSVP
                  without creating an account, and you decide who is approved to attend.
                </p>
              </div>
            </article>
            <article className="home-step">
              <span className="home-step-num" aria-hidden="true">
                03
              </span>
              <div>
                <h3>Guests buy their tickets</h3>
                <p>
                  Guests pay securely through Stripe. Once approved, their ticket confirms
                  their place at your wedding.
                </p>
              </div>
            </article>
            <article className="home-step">
              <span className="home-step-num" aria-hidden="true">
                04
              </span>
              <div>
                <h3>Fund the wedding</h3>
                <p>
                  Ticket payments are sent to your connected Stripe account, helping you
                  cover the cost of your wedding as your guest list comes together.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
